import { SessionExpireeError, getApi } from '@/lib/api';
import { lireSession } from '@/lib/session';
import type { Session, Traduction } from '@rosetta/shared';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

/**
 * A translation that is still running is read again at this pace: every 5 s, like the history
 * and the follow-up of the translations (#7: it was every second while the input screen waited).
 */
export const INTERVALLE_LECTURE_MS = 5000;

/**
 * The one query key of a translation. The input screen, the result and the analysis all
 * read the same translation: sharing the key is what lets a screen find what the previous
 * one already fetched.
 */
export const cleTraduction = (id: string) => ['traduction', id] as const;

export interface OptionsLectureTraduction {
  /** When to try again after a failed read: each screen says what it can live with. */
  retry: NonNullable<UseQueryOptions<Traduction, Error>['retry']>;
  /**
   * The session to read with. By default the stored one, looked up at every read: the route
   * guard lets a session through, but it can expire while the page is open.
   */
  session?: Session;
  /** How long a read stays fresh. Defaults to the one of the query client. */
  staleTime?: number;
  /**
   * Keep reading a running translation after a read failed, as long as the last good read
   * said "en cours" (the screen then waits for the service to come back). Off by default:
   * the reading stops on an error, and "réessayer" is the user's call.
   */
  poursuivreSurErreur?: boolean;
  /** Pace of the reads of a running translation, in ms; `INTERVALLE_LECTURE_MS` when omitted (tests go faster). */
  intervalleMs?: number;
}

/**
 * Reads a translation through the `RosettaApi` (never a `fetch` from a component), and
 * reads it again every 5 seconds while its status is `en_cours` or while noyaux of its document
 * are being translated by another launch (#36). The domain errors
 * (`TraductionIntrouvableError`, `SessionExpireeError`, `ServiceIndisponibleError`) come
 * out untouched in `error`: the screen decides what each one looks like.
 */
export function useLectureTraduction(id: string, options: OptionsLectureTraduction) {
  const {
    retry,
    session,
    staleTime,
    poursuivreSurErreur = false,
    intervalleMs = INTERVALLE_LECTURE_MS,
  } = options;
  return useQuery<Traduction, Error>({
    queryKey: cleTraduction(id),
    queryFn: () => {
      const courante = session ?? lireSession();
      if (courante === null) throw new SessionExpireeError();
      return getApi().lireTraduction(courante, id);
    },
    ...(staleTime === undefined ? {} : { staleTime }),
    retry,
    // Also while another launch of the document translates a noyau (#36): its spinner turns into a
    // normal tab when that launch ends, without a reload.
    refetchInterval: (requete) =>
      (poursuivreSurErreur || requete.state.status !== 'error') &&
      (requete.state.data?.statut === 'en_cours' ||
        (requete.state.data?.noyauxEnCours?.length ?? 0) > 0)
        ? intervalleMs
        : false,
  });
}
